/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The pricing model of an offer — ONE normaliser, ONE sanitiser and ONE cost
 * function for every surface (docs/plan-aanbodmodellen-2026-10-15.md).
 *
 * Until 2026-10 an offer was a flat commission % plus service costs, with two
 * flags for the exceptions (`zeroCommission`, `fixedRatePayout`). Two
 * providers then started charging by volume — a staffel (Lekkerladen) and a
 * commission plus a vastrecht per kWh with a yearly floor and cap
 * (Groen-laden) — and a flat % ranks both wrongly at every volume but one.
 * So a contract may carry `pricing` (src/types/api/offerPricing.ts), and
 * everything that reads a percentage — the tier word, the sort, the
 * commission rank, the overview's "vanaf" — reads it through this module:
 *
 *   normalizeOfferPricing  the record (pricing, else the two legacy flags, else
 *                          `commissionFee`) → one model, or null = unknown
 *   sanitizeOfferPricing   what a save or a proposal may store
 *   offerCostCents         what the model costs at a volume, in whole cents
 *   effectiveCommissionPct that cost over the yield after the market fee
 *   nominalCommissionPct   the percentage a row SHOWS at a volume
 *   visibleCommissionFee   the one percentage an offer is known by — the
 *                          NOMINAL % at the audience's reference volume
 *   contractWithPricing    the one writer of a model onto a record
 *
 * It also holds the yield chain the cost function stands on — the reference
 * volumes, the ERE count of a volume, the fallback price and the market fee —
 * which src/lib/earnings.ts re-exports under the names its callers always
 * used: the server and the bake need them too and cannot load TypeScript.
 *
 * The rekenbasis is the site convention for every model (besluit 3): a
 * percentage applies to the gross yield minus the 1,5% market fee, also for a
 * provider that states its own % over the gross.
 *
 * Plain ESM JavaScript, no imports: server, bake and client share it.
 */

// ——— The yield chain ———————————————————————————————————————————————————

/**
 * Default annual charging volume (kWh/year) — the comparison's initial
 * `volume` and the static estimates: 3 000 kWh since 2026-10-30 (Jasper: the
 * standard comparison is 1 jaar at 3 000 kWh; 6 000 from 2026-10-16, 2 500
 * before). The public URL contract in public/llms.txt documents this value —
 * update it in sync.
 */
export const DEFAULT_ANNUAL_KWH = 3000;

/**
 * The business default — 6 000 kWh since 2026-10-16 (Jasper: the comparison
 * started at 6 000 kWh for every audience; until then 5 000, one company car
 * at 25 000 km a year). It stayed when the private default moved to 3 000 on
 * 2026-10-30: that ask named the standard comparison, which is a household's.
 * Its own constant, so the two part without touching a caller; the fleet
 * estimator (src/lib/kwhEstimate.ts) scales a fleet from its own inputs, not
 * from here.
 */
export const DEFAULT_ANNUAL_KWH_BUSINESS = 6000;

/**
 * ONE reference household and ONE reference business charge point, used by
 * every worked example on the site and by the percentage a volume-dependent
 * offer is known by (visibleCommissionFee).
 */
export const REFERENCE_KWH = Object.freeze({
  private: DEFAULT_ANNUAL_KWH,
  business: DEFAULT_ANNUAL_KWH_BUSINESS,
});

/**
 * The volume a surface calculates with when the visitor has not typed one:
 * the audience decides. `'Alle'`, null and anything unknown take the
 * consumer default, because that is who reads the page.
 *
 * @param {unknown} [audience]
 * @returns {number}
 */
export function defaultAnnualKwh(audience) {
  return audience === 'Zakelijk' ? DEFAULT_ANNUAL_KWH_BUSINESS : DEFAULT_ANNUAL_KWH;
}

/**
 * €/ERE — FALLBACK only, for an environment without a price series. The
 * leading figure is the reading in force in DATA_DIR/ere_price_history.json
 * (src/lib/erePrice.js).
 */
export const ERE_PRICE = 0.49;
export const MARKET_FEE_PERCENT = 1.5;
export const KWH_TO_ERE_FACTOR = 0.3327;

/**
 * Whole EREs for a charged volume. Integer arithmetic (3 327 / 10 000), so the
 * result is deterministic and rounds half up: 2 500 kWh → 831,75 → 832;
 * 3 000 kWh → 998,1 → 998; 5 000 kWh → 1 663,5 → 1 664; 6 000 kWh → 1 996,2 →
 * 1 996. Rounded here and nowhere else.
 *
 * @param {number} kwh
 * @returns {number}
 */
export function ereCountFor(kwh) {
  return Math.round((Math.round(kwh) * 3327) / 10000);
}

/**
 * The first two lines of the cent chain (src/lib/earnings.ts): the gross yield
 * of a whole ERE count and the market fee on that printed gross. Cents are
 * integers; the price is taken in ten-thousandths (a reading is capped at four
 * decimals), so 832 × 0,445 is 832 × 4 450 = 37 024 cents exactly.
 *
 * @param {number} eres
 * @param {number} erePrice €/ERE
 * @param {number} [marketFeePct]
 * @returns {{ grossCents: number, marketCents: number }}
 */
export function yieldCents(eres, erePrice, marketFeePct = MARKET_FEE_PERCENT) {
  const priceTenThousandths = Math.round(erePrice * 10000);
  const grossCents = Math.round((eres * priceTenThousandths) / 100);
  const marketCents = Math.round((grossCents * Math.round(marketFeePct * 100)) / 10000);
  return { grossCents, marketCents };
}

/**
 * A percentage of a cent amount, to the cent — the commission line of the
 * chain. The % is taken in hundredths, like earningsFromEres always did.
 *
 * @param {number} baseCents
 * @param {number} pct
 * @returns {number}
 */
function pctOfCents(baseCents, pct) {
  return Math.round((baseCents * Math.round(pct * 100)) / 10000);
}

/**
 * A rate in € per kWh times a volume, to the cent. The rate is taken in
 * ten-thousandths (sanitised to four decimals), so 2 500 kWh × € 0,0057 is
 * 2 500 × 57 / 100 = 1 425 cents exactly.
 *
 * @param {number} kwh
 * @param {number} eurPerKwh
 * @returns {number}
 */
function kwhAmountCents(kwh, eurPerKwh) {
  return Math.round((Math.round(kwh) * Math.round(eurPerKwh * 10000)) / 100);
}

/** @param {number} x @returns {number} */
const round1 = (x) => Math.round(x * 10) / 10;

// ——— The models ——————————————————————————————————————————————————————————

/** @typedef {import('../types/api/offerPricing.js').OfferPricing} OfferPricing */
/** @typedef {import('../types/api/offerPricing.js').OfferPricingModel} OfferPricingModel */
/** @typedef {import('../types/api/offerPricing.js').OfferPricingTier} OfferPricingTier */
/** @typedef {import('../types/api/offerPricing.js').OfferCost} OfferCost */
/** @typedef {import('../types/api/offerPricing.js').OfferPricingSanitized} OfferPricingSanitized */

/** The five models, in the order the editor offers them. */
export const OFFER_PRICING_MODELS = /** @type {readonly OfferPricingModel[]} */ (
  Object.freeze(['commission', 'subscription', 'fixed_rate', 'tiered', 'commission_plus_kwh_fee'])
);

/** Bands a staffel may have. */
export const OFFER_PRICING_TIERS_MIN = 2;
export const OFFER_PRICING_TIERS_MAX = 8;

/**
 * A posted number: a finite number, or a string that is a plain decimal (the
 * admin form posts strings): digits, optionally a sign and a fraction, the
 * comma read as a decimal point ("14,5" is 14.5, as a Dutch keyboard types
 * it). Anything else — '', ' ', null, a word, and the spellings Number() would
 * still read ("0x10", "1e1") — is null.
 *
 * @param {unknown} v
 * @returns {number | null}
 */
function postedNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const text = v.trim();
    if (!/^-?\d+([.,]\d+)?$/.test(text)) return null;
    return Number(text.replace(',', '.'));
  }
  return null;
}

/**
 * The value at most `decimals` decimals, rounded to exactly that — or null
 * when it carries more (12,34% on a one-decimal field is refused, not rounded:
 * a silently changed price is worse than a refusal).
 *
 * @param {number} x
 * @param {number} decimals
 * @returns {number | null}
 */
function withDecimals(x, decimals) {
  const scale = 10 ** decimals;
  const scaled = x * scale;
  if (Math.abs(scaled - Math.round(scaled)) > 1e-6) return null;
  return Math.round(scaled) / scale;
}

/**
 * A percentage 0–100 with at most one decimal, or null.
 * @param {unknown} v
 * @returns {number | null}
 */
function sanePct(v) {
  const n = postedNumber(v);
  if (n === null || n < 0 || n > 100) return null;
  return withDecimals(n, 1);
}

/**
 * A rate in € per kWh, 0–1 with at most four decimals, or null.
 * @param {unknown} v
 * @returns {number | null}
 */
function saneRate(v) {
  const n = postedNumber(v);
  if (n === null || n < 0 || n > 1) return null;
  return withDecimals(n, 4);
}

/**
 * A yearly bound: null (absent, null or an emptied input) = no bound; a
 * number ≥ 0 with at most two decimals; `undefined` = invalid.
 * @param {unknown} v
 * @returns {number | null | undefined}
 */
function saneBound(v) {
  if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) return null;
  const n = postedNumber(v);
  if (n === null || n < 0) return undefined;
  const r = withDecimals(n, 2);
  return r === null ? undefined : r;
}

/**
 * What a save or a proposal may store as `pricing`: the model with only its
 * own keys, in the type's key order, numbers coerced and rounded to their
 * precision — or the first rule it breaks. Percentages 0–100 with one
 * decimal; `eurPerKwh` 0–1 with four (a vastrecht may be left empty — null —
 * when a yearly bound is set: `kwh_fee_or_bound_required` otherwise); a staffel of 2–8 bands whose `fromKwh`
 * are whole, strictly ascending and start at 0; yearly bounds null or ≥ 0
 * with two decimals and min ≤ max. A flat commission of 0% is refused
 * (`zero_is_subscription`): a genuine 0% is the subscription model, and a
 * stored `commission` of 0 would win "lowest commission" without being one.
 *
 * @param {unknown} raw
 * @returns {OfferPricingSanitized}
 */
export function sanitizeOfferPricing(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, reason: 'not_an_object' };
  }
  const r = /** @type {Record<string, unknown>} */ (raw);
  switch (r.model) {
    case 'commission': {
      const commissionPct = sanePct(r.commissionPct);
      if (commissionPct === null) return { ok: false, reason: 'commission_pct_invalid' };
      if (commissionPct === 0) return { ok: false, reason: 'zero_is_subscription' };
      return { ok: true, pricing: { model: 'commission', commissionPct } };
    }
    case 'subscription':
      return { ok: true, pricing: { model: 'subscription' } };
    case 'fixed_rate': {
      const eurPerKwh = saneRate(r.eurPerKwh);
      if (eurPerKwh === null) return { ok: false, reason: 'eur_per_kwh_invalid' };
      return { ok: true, pricing: { model: 'fixed_rate', eurPerKwh } };
    }
    case 'tiered': {
      const list = Array.isArray(r.tiers) ? r.tiers : null;
      if (!list || list.length < OFFER_PRICING_TIERS_MIN || list.length > OFFER_PRICING_TIERS_MAX) {
        return { ok: false, reason: 'tiers_count' };
      }
      /** @type {OfferPricingTier[]} */
      const tiers = [];
      for (const item of list) {
        const t =
          item && typeof item === 'object' ? /** @type {Record<string, unknown>} */ (item) : {};
        const fromKwh = postedNumber(t.fromKwh);
        if (fromKwh === null || !Number.isInteger(fromKwh) || fromKwh < 0) {
          return { ok: false, reason: 'tier_kwh_invalid' };
        }
        if (tiers.length === 0 && fromKwh !== 0)
          return { ok: false, reason: 'tier_first_not_zero' };
        if (tiers.length > 0 && fromKwh <= tiers[tiers.length - 1].fromKwh) {
          return { ok: false, reason: 'tiers_not_ascending' };
        }
        const commissionPct = sanePct(t.commissionPct);
        if (commissionPct === null) return { ok: false, reason: 'tier_pct_invalid' };
        tiers.push({ fromKwh, commissionPct });
      }
      return { ok: true, pricing: { model: 'tiered', tiers } };
    }
    case 'commission_plus_kwh_fee': {
      const commissionPct = sanePct(r.commissionPct);
      if (commissionPct === null) return { ok: false, reason: 'commission_pct_invalid' };
      // An emptied vastrecht is no vastrecht: a commission with a yearly
      // minimum and/or maximum for the customer (2026-10-16).
      const feeEmpty =
        r.eurPerKwh === undefined ||
        r.eurPerKwh === null ||
        (typeof r.eurPerKwh === 'string' && r.eurPerKwh.trim() === '');
      const eurPerKwh = feeEmpty ? null : saneRate(r.eurPerKwh);
      if (!feeEmpty && eurPerKwh === null) return { ok: false, reason: 'eur_per_kwh_invalid' };
      const yearlyMin = saneBound(r.yearlyMin);
      if (yearlyMin === undefined) return { ok: false, reason: 'yearly_min_invalid' };
      const yearlyMax = saneBound(r.yearlyMax);
      if (yearlyMax === undefined) return { ok: false, reason: 'yearly_max_invalid' };
      if (yearlyMin !== null && yearlyMax !== null && yearlyMin > yearlyMax) {
        return { ok: false, reason: 'yearly_bounds_inverted' };
      }
      if (eurPerKwh === null && yearlyMin === null && yearlyMax === null) {
        return { ok: false, reason: 'kwh_fee_or_bound_required' };
      }
      return {
        ok: true,
        pricing: {
          model: 'commission_plus_kwh_fee',
          commissionPct,
          eurPerKwh,
          yearlyMin,
          yearlyMax,
        },
      };
    }
    default:
      return { ok: false, reason: 'unknown_model' };
  }
}

/**
 * A contract record with a (sanitised) model written onto it — THE one writer
 * of a model, for the admin save and the approval of a portal proposal. A
 * copy; the input is not touched. The two legacy flags (`fixedRatePayout`,
 * `zeroCommission`) are always gone: the model is the one source from then on
 * (besluit 1), so a flag can never disagree with it.
 *
 *   commission  a flat % lives in the bare `commissionFee` ONLY: the % is
 *               stored there and `pricing` is deleted. A stored model exists
 *               for the four deviating models alone, so a record never holds
 *               two spellings of the market norm.
 *   any other   `pricing` is stored and the headline `commissionFee` is set to
 *               the model's NOMINAL % at the audience's reference volume
 *               (nominalCommissionPct; a fixed rate at the fallback price), so
 *               a reader of the bare field (a CSV, an older client) sees what
 *               the site prints. A subscription is 0 and keeps the legacy
 *               genuine-0 shape (`zeroCommission: true`), because a bare 0
 *               alone reads as "not filled in".
 *
 * @param {Record<string, unknown>} contract
 * @param {OfferPricing} pricing  a sanitizeOfferPricing result
 * @returns {Record<string, unknown>}
 */
export function contractWithPricing(contract, pricing) {
  /** @type {Record<string, unknown>} */
  const next = { ...contract };
  delete next.fixedRatePayout;
  delete next.zeroCommission;
  delete next.pricing;
  if (pricing.model === 'commission') {
    next.commissionFee = pricing.commissionPct;
    return next;
  }
  const headline = nominalCommissionPct(pricing, defaultAnnualKwh(next.targetAudience), ERE_PRICE);
  if (headline !== null) next.commissionFee = headline;
  if (pricing.model === 'subscription') next.zeroCommission = true;
  return { ...next, pricing };
}

/**
 * The contract's own `pricing`, sanitised — or null when it has none or one
 * that does not pass (a stored model that fails the rules is no model).
 *
 * @param {unknown} contract
 * @returns {OfferPricing | null}
 */
export function ownOfferPricing(contract) {
  if (!contract || typeof contract !== 'object') return null;
  const raw = /** @type {Record<string, unknown>} */ (contract).pricing;
  if (raw === undefined || raw === null) return null;
  const sanitized = sanitizeOfferPricing(raw);
  return sanitized.ok ? sanitized.pricing : null;
}

/**
 * The ONE model of a contract, read from either shape the record holds:
 *
 *   its own `pricing`, sanitised        when present and accepted by
 *                                       sanitizeOfferPricing — the SAME rules a
 *                                       save applies, so a stored model the save
 *                                       would refuse is no model here either
 *   subscription                        zeroCommission === true && commissionFee === 0
 *   commission                          commissionFee a positive number — also
 *                                       under a `fixedRatePayout` flag, which
 *                                       without a valid full `pricing` is only a
 *                                       label: the hand-typed % is the commission
 *                                       (deviatingModelOf still names it)
 *   null                                anything else — unknown, never 0
 *
 * A bare `commissionFee: 0` is the record's legacy "not filled in" sentinel
 * (only the flag makes it a genuine 0%), so it reads as null. The stored % of a
 * flat offer is passed through as it is — no rounding — so the 70+ flat
 * offers read exactly as before.
 *
 * @param {unknown} contract
 * @returns {OfferPricing | null}
 */
export function normalizeOfferPricing(contract) {
  if (!contract || typeof contract !== 'object') return null;
  const c = /** @type {Record<string, unknown>} */ (contract);
  const own = ownOfferPricing(c);
  if (own) return own;
  if (c.zeroCommission === true && c.commissionFee === 0) return { model: 'subscription' };
  const fee = c.commissionFee;
  if (typeof fee === 'number' && Number.isFinite(fee) && fee > 0) {
    return { model: 'commission', commissionPct: fee };
  }
  return null;
}

/**
 * The band of a staffel a volume falls in: the highest `fromKwh` ≤ the volume
 * (the boundary belongs to the band it opens — 3 500 kWh is the second band).
 *
 * @param {OfferPricingTier[]} tiers ascending, the first from 0
 * @param {number} kwh
 * @returns {OfferPricingTier}
 */
export function tierFor(tiers, kwh) {
  const volume = Math.round(kwh);
  let band = tiers[0];
  for (const tier of tiers) if (tier.fromKwh <= volume) band = tier;
  return band;
}

/**
 * What an offer costs at one volume, in whole cents, from the chain's two
 * printed lines (yieldCents): the commission on (gross − market), a vastrecht
 * per kWh, and the yearly clamp.
 *
 *   commission               pct × (gross − market)
 *   subscription             0 (the offer's service costs are its price)
 *   fixed_rate               (gross − market) − kWh × rate: what the provider
 *                            keeps of the yield — NEGATIVE when the rate is
 *                            above the yield (the provider pays the visitor
 *                            more than the ERE yield: the net is the rate)
 *   tiered                   the band's pct, then as commission
 *   commission_plus_kwh_fee  commission + kWh × rate (none without a vastrecht),
 *                            clamped to the yearly bounds (a null bound does
 *                            not clamp)
 *
 * @param {{ pricing: OfferPricing, kwh: number, grossCents: number, marketCents: number }} input
 * @returns {OfferCost}
 */
export function offerCostCents({ pricing, kwh, grossCents, marketCents }) {
  const base = grossCents - marketCents;
  /** @param {number} commissionCents @returns {OfferCost} */
  const plain = (commissionCents) => ({
    commissionCents,
    kwhFeeCents: 0,
    clampedCents: 0,
    totalCents: commissionCents,
  });
  switch (pricing.model) {
    case 'commission':
      return plain(pctOfCents(base, pricing.commissionPct));
    case 'subscription':
      return plain(0);
    case 'fixed_rate':
      return plain(base - kwhAmountCents(kwh, pricing.eurPerKwh));
    case 'tiered':
      return plain(pctOfCents(base, tierFor(pricing.tiers, kwh).commissionPct));
    case 'commission_plus_kwh_fee': {
      const commissionCents = pctOfCents(base, pricing.commissionPct);
      const kwhFeeCents = pricing.eurPerKwh === null ? 0 : kwhAmountCents(kwh, pricing.eurPerKwh);
      const sum = commissionCents + kwhFeeCents;
      let totalCents = sum;
      if (pricing.yearlyMin !== null)
        totalCents = Math.max(totalCents, Math.round(pricing.yearlyMin * 100));
      if (pricing.yearlyMax !== null)
        totalCents = Math.min(totalCents, Math.round(pricing.yearlyMax * 100));
      return { commissionCents, kwhFeeCents, clampedCents: totalCents - sum, totalCents };
    }
    default:
      return plain(0);
  }
}

/**
 * The yield the percentages apply to, at a volume and a price: gross − market
 * fee, in cents, with the two lines it came from.
 *
 * @param {number} kwh
 * @param {number} erePrice
 * @param {number} [marketFeePct]
 * @returns {{ grossCents: number, marketCents: number, baseCents: number }}
 */
function baseAt(kwh, erePrice, marketFeePct = MARKET_FEE_PERCENT) {
  const { grossCents, marketCents } = yieldCents(ereCountFor(kwh), erePrice, marketFeePct);
  return { grossCents, marketCents, baseCents: grossCents - marketCents };
}

/**
 * What the offer really costs at a volume, as a percentage of the yield after
 * the market fee, to one decimal — the number the tier word, the sort and the
 * crown compare (the surfaces with a volume AND a price; the stable scalar of
 * an offer is visibleCommissionFee). Negative for a fixed rate above the
 * yield: the lowest is the best. Null for an unknown model, or when there is
 * no yield to divide by.
 *
 * @param {OfferPricing | null} pricing
 * @param {number} kwh
 * @param {number} erePrice €/ERE
 * @param {number} [marketFeePct]
 * @returns {number | null}
 */
export function effectiveCommissionPct(pricing, kwh, erePrice, marketFeePct = MARKET_FEE_PERCENT) {
  if (!pricing) return null;
  const { grossCents, marketCents, baseCents } = baseAt(kwh, erePrice, marketFeePct);
  if (baseCents <= 0) return null;
  const { totalCents } = offerCostCents({ pricing, kwh, grossCents, marketCents });
  return round1((totalCents / baseCents) * 100);
}

/**
 * The percentage a row SHOWS at a volume: the band's % of a staffel, the
 * nominal % of a commission (with or without a vastrecht beside it), 0 for a
 * subscription, and for a fixed rate the % derived at that price — one
 * decimal, floored at 0 (the effective % may be negative; a row never prints
 * one). Null for an unknown model (or a fixed rate with no yield to derive
 * from).
 *
 * @param {OfferPricing | null} pricing
 * @param {number} kwh
 * @param {number} [erePrice] the price in €/ERE — read by a fixed rate only
 * @returns {number | null}
 */
export function nominalCommissionPct(pricing, kwh, erePrice = ERE_PRICE) {
  if (!pricing) return null;
  switch (pricing.model) {
    case 'commission':
    case 'commission_plus_kwh_fee':
      return pricing.commissionPct;
    case 'subscription':
      return 0;
    case 'tiered':
      return tierFor(pricing.tiers, kwh).commissionPct;
    case 'fixed_rate': {
      const derived = effectiveCommissionPct(pricing, kwh, erePrice);
      return derived === null ? null : Math.max(0, derived);
    }
    default:
      return null;
  }
}

/**
 * Whether the cost of the model depends on the volume — a staffel, a
 * vastrecht per kWh (or a commission with a yearly floor or cap: where it
 * bites moves with the volume) and a fixed rate do; a flat % and a
 * subscription do not.
 *
 * @param {OfferPricing | null} pricing
 * @returns {boolean}
 */
export function pricingIsVolumeDependent(pricing) {
  return (
    pricing?.model === 'tiered' ||
    pricing?.model === 'commission_plus_kwh_fee' ||
    pricing?.model === 'fixed_rate'
  );
}

/**
 * Whether a commission-plus-vastrecht model has no vastrecht: a commission
 * with a yearly minimum and/or maximum for the customer (2026-10-16). Its
 * words differ everywhere ('capped' in src/lib/offerPricingText.ts).
 *
 * @param {OfferPricing | null | undefined} pricing
 * @returns {boolean}
 */
export function isCappedCommission(pricing) {
  return pricing?.model === 'commission_plus_kwh_fee' && pricing.eurPerKwh === null;
}

/**
 * The ONE percentage an offer is known by, or null when it is unknown: the
 * NOMINAL % at the audience's reference volume (3 000 kWh private, 6 000 business) —
 * the stored % of a flat offer as it is, the band of a staffel, the commission
 * of a commission plus a vastrecht, 0 for a subscription (genuine 0%), and for
 * a fixed rate the % derived at the given price (ERE_PRICE without one).
 *
 * Nominal, not effective, on purpose: this scalar is what the public payloads
 * put in `commissionFee`, what the nightly commission rank and the overview's
 * "vanaf" compare, and what the admin's dashboard and completeness check read.
 * All of them must be stable across the weekly ERE readings (a vastrecht's
 * effective % moves with the price, its nominal % does not) and equal to the
 * percentage the site prints. The effective % stays for the surfaces that have
 * a volume AND a price: the tier word, the sort, the crown and the net. A bare
 * 0 is "not filled in" in all of them, and a staffel is a filled-in offer in
 * all of them.
 *
 * @param {unknown} contract  a contract record (any shape)
 * @param {number} [erePrice] the price in €/ERE — read by a fixed rate only
 * @returns {number | null}
 */
export function visibleCommissionFee(contract, erePrice = ERE_PRICE) {
  const pricing = normalizeOfferPricing(contract);
  if (!pricing) return null;
  const audience = /** @type {Record<string, unknown>} */ (contract).targetAudience;
  return nominalCommissionPct(pricing, defaultAnnualKwh(audience), erePrice);
}

// ——— The public row ————————————————————————————————————————————————————————

/**
 * The lowest percentage of a staffel — what the aanbiedersoverzicht's
 * "vanaf" and the homepage's JSON-LD name a staffel by.
 *
 * @param {OfferPricingTier[]} tiers
 * @returns {number}
 */
export function lowestTierPct(tiers) {
  return Math.min(...tiers.map((t) => t.commissionPct));
}

/**
 * The pricing model of a PUBLIC row — a comparer row, a profile contract, a
 * compare page's offer — ONE reading for every surface that prints or
 * computes one: its `pricing` when the payload carries one (only an offer
 * that is not a flat commission does, src/lib/providerPayloads.js),
 * sanitised, so a malformed model in a stale cache is no model; else a flat
 * commission at its `commissionFee` when that is a number (0 included: a
 * genuine 0% reaches the payload only as a subscription); else null —
 * unknown, never 0. A row whose `pricing` does not pass falls back to its
 * `commissionFee`, the nominal % at the audience's reference volume.
 *
 * @param {{ pricing?: unknown, commissionFee?: unknown } | null | undefined} row
 * @returns {OfferPricing | null}
 */
export function rowOfferPricing(row) {
  if (!row || typeof row !== 'object') return null;
  if (row.pricing !== undefined && row.pricing !== null) {
    const own = ownOfferPricing(row);
    if (own) return own;
  }
  const fee = row.commissionFee;
  return typeof fee === 'number' && Number.isFinite(fee)
    ? { model: 'commission', commissionPct: fee }
    : null;
}

/**
 * The percentage a public row SHOWS at a volume (nominalCommissionPct of
 * rowOfferPricing): a flat row's `commissionFee` exactly, a staffel's band, a
 * vastrecht offer's nominal %; null when unknown.
 *
 * @param {{ pricing?: unknown, commissionFee?: unknown } | null | undefined} row
 * @param {number} kwh
 * @param {number} [erePrice] the price in €/ERE — read by a fixed rate only
 * @returns {number | null}
 */
export function shownCommissionPct(row, kwh, erePrice = ERE_PRICE) {
  return nominalCommissionPct(rowOfferPricing(row), kwh, erePrice);
}
